import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  type CreateMeetingParams,
  type ScheduledMeeting,
  useScheduledMeetingsMutations,
  useScheduledMeetingsQuery,
} from '../../../contexts';
import { useErrorHandler } from '../../../hooks/useErrorHandler';
import { GENERATE_UNIQUE_MEETING_ID } from '../helpers/constants';
import {
  buildMeetingIdOptions,
  getPersonalMeetings,
  getSelectedMeetingIdValue,
} from '../helpers/meetingIdOptions';
import type { UseMeetingIdSelectionProps } from '../types';

const NEW_MEETING_PARAMS: CreateMeetingParams = { description: null, password: '' };

/**
 * Backs the meeting-ID dropdown: the user's personal meetings as options, plus
 * "Generate a unique meeting ID" last. Picking a personal meeting schedules that room;
 * picking the unique option allocates one, reusing the room already allocated for
 * this session so toggling back and forth does not leave orphan rooms behind.
 * The closed control selects the default personal meeting when one exists,
 * otherwise Generate.
 */
const useMeetingIdSelection = ({
  isOpen,
  room,
  meeting,
  setMeeting,
  toastHelpers,
  defaultMeetingId,
}: UseMeetingIdSelectionProps) => {
  const {
    meetings,
    isLoading,
    isInitialized,
    error: listError,
    getMeetings,
  } = useScheduledMeetingsQuery();
  const { create } = useScheduledMeetingsMutations();
  const { handleApiError, handleComponentError } = useErrorHandler('ScheduleMeetingModal');
  const [isAllocating, setIsAllocating] = useState(false);
  const allocatedMeetingRef = useRef<ScheduledMeeting | undefined>(undefined);
  const hasRequestedMeetingsRef = useRef(false);

  // One request per open. A failure leaves `isInitialized` false while flipping
  // `isLoading` back, which would otherwise retrigger this effect on every
  // render and hammer the endpoint for as long as the modal stays open.
  useEffect(() => {
    if (!isOpen || isInitialized || isLoading || hasRequestedMeetingsRef.current) {
      return;
    }
    hasRequestedMeetingsRef.current = true;
    void getMeetings().catch((fetchError) => {
      handleComponentError(fetchError as Error, 'meeting_id_options_fetch');
    });
  }, [isOpen, isInitialized, isLoading, getMeetings, handleComponentError]);

  useEffect(() => {
    if (!isOpen) {
      allocatedMeetingRef.current = undefined;
      // Cleared on close so the next open retries a list that failed.
      hasRequestedMeetingsRef.current = false;
      setIsAllocating(false);
    }
  }, [isOpen]);

  const personalMeetings = useMemo(
    () => getPersonalMeetings(meetings, defaultMeetingId),
    [meetings, defaultMeetingId],
  );

  // A scheduled room that is not one of the personal meetings was allocated by the
  // modal itself — either on open or by the unique option. Remember it so that
  // reselecting the unique option reuses the room instead of allocating another.
  useEffect(() => {
    if (!(isOpen && isInitialized && meeting)) {
      return;
    }
    if (personalMeetings.some((personal) => personal.room === meeting.room)) {
      return;
    }
    allocatedMeetingRef.current = meeting;
  }, [isOpen, isInitialized, meeting, personalMeetings]);

  const options = useMemo(
    () => buildMeetingIdOptions(personalMeetings, defaultMeetingId),
    [personalMeetings, defaultMeetingId],
  );

  const selectedValue = useMemo(
    () => getSelectedMeetingIdValue(room, personalMeetings),
    [room, personalMeetings],
  );

  const handleChange = useCallback(
    async (value: string) => {
      if (value === selectedValue) {
        return;
      }

      if (value !== GENERATE_UNIQUE_MEETING_ID) {
        const selected = personalMeetings.find((personal) => personal.room === value);
        if (selected) {
          setMeeting(selected);
        }
        return;
      }

      if (allocatedMeetingRef.current) {
        setMeeting(allocatedMeetingRef.current);
        return;
      }

      setIsAllocating(true);
      try {
        const allocated = await create(NEW_MEETING_PARAMS);
        allocatedMeetingRef.current = allocated;
        setMeeting(allocated);
      } catch (error) {
        const errorInstance = error instanceof Error ? error : new Error(String(error));
        toastHelpers.showError('Failed to allocate meeting', errorInstance.message);
        handleApiError(errorInstance, 'createSchedule', 'POST');
      } finally {
        setIsAllocating(false);
      }
    },
    [selectedValue, personalMeetings, setMeeting, create, toastHelpers, handleApiError],
  );

  return {
    options,
    selectedValue,
    // A failed list still leaves "Generate a unique meeting ID" usable, so stop
    // reporting as loading rather than disabling the dropdown for good.
    isLoading: isLoading || !(isInitialized || Boolean(listError)),
    isAllocating,
    handleChange,
  };
};

export default useMeetingIdSelection;
